import type { MePayload, Organization, UserWire } from './types';
import { normalizeAccess } from './normalizeAccess';

interface JwtPayload extends MePayload {
  exp?: number;
  sub?: string;
  iss?: string;
  aud?: string;
  jti?: string;
  iat?: number;
}

function decodeBase64Url(value: string): string {
  let base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) base64 += '='.repeat(4 - pad);
  return atob(base64);
}

function readJwtPayload(token: string): JwtPayload | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    return JSON.parse(decodeBase64Url(segment)) as JwtPayload;
  } catch {
    return null;
  }
}

function normalizeOrganization(raw: unknown): Organization | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.id !== 'string' || typeof item.name !== 'string') return null;
  return {
    id: item.id,
    name: item.name,
    ascend_id: typeof item.ascend_id === 'string' ? item.ascend_id : null,
  };
}

function normalizeUserWire(raw: unknown): UserWire | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.id !== 'string' || typeof item.email !== 'string') return null;
  return {
    id: item.id,
    name: typeof item.name === 'string' ? item.name : '',
    last_name: typeof item.last_name === 'string' ? item.last_name : null,
    email: item.email,
    phone: typeof item.phone === 'string' ? item.phone : null,
    avatar_url: typeof item.avatar_url === 'string' ? item.avatar_url : null,
    ascend_id: typeof item.ascend_id === 'string' ? item.ascend_id : null,
  };
}

function normalizeLocations(raw: unknown): MePayload['locations'] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item) => ({
      id: typeof item.id === 'string' ? item.id : '',
      name: typeof item.name === 'string' ? item.name : '',
      ascend_id: typeof item.ascend_id === 'string' ? item.ascend_id : null,
    }))
    .filter((item) => item.id.length > 0);
}

/**
 * The access token body **is** the `/me` payload plus standard claims, so the shell can
 * render on boot without a network call.
 */
export function parseAccessTokenPayload(token: string): MePayload | null {
  const payload = readJwtPayload(token);
  if (!payload) return null;

  if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) {
    return null;
  }

  const organization = normalizeOrganization(payload.organization);
  if (!organization) return null;

  const me: MePayload = {
    organization,
    locations: normalizeLocations(payload.locations),
  };

  const user = normalizeUserWire(payload.user);
  const admin = normalizeUserWire(payload.admin);
  if (user) me.user = user;
  if (admin) me.admin = admin;

  // Distinguish "unscoped" (`access: null`) from an organization account (key absent).
  if ('access' in payload) {
    me.access = normalizeAccess(payload.access);
  }

  return me;
}

export function readAccessTokenExpiry(token: string): number | null {
  const payload = readJwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
}
